import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { FormGroup, FormText, Input, Label } from 'reactstrap';

import { sortByWeight } from '@techchange/dnd';
import { messages } from '../../../_shared/i18n/messages';

const propTypes = {

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * ID of the element that describes this checkbox group (optional).
	 */
	['aria-describedby']: PropTypes.string,

	/**
	 * The input value.
	 */
	value: PropTypes.arrayOf(PropTypes.string),

	/**
	 * The input options (if any).
	 */
	options: PropTypes.arrayOf(PropTypes.shape({

		id: PropTypes.string.isRequired,

		label: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.element,
			PropTypes.array,
			PropTypes.shape({}),
		]),

		value: PropTypes.string.isRequired,

		help: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),

		weight: PropTypes.number,

	})),

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * True, if the input value is disabled.
	 */
	disabled: PropTypes.bool,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func,

};

const CheckboxesInput = (props) => {

	const {
		name,
		value,
		options,
		isEditing,
		isInvalid,
		disabled,
		onChange,
	} = props;

	// Sort the options
	const sortedOptions = sortByWeight(options);

	if (!isEditing) {

		// Sort the values based on the sorted options
		const sortedValues = value ? [...value] : [];

		if (sortedValues.length > 1) {

			sortedValues.sort((value1, value2) => {

				const optionIndex1 = sortedOptions.findIndex((option) => option.value === value1);
				const optionIndex2 = sortedOptions.findIndex((option) => option.value === value2);

				return optionIndex1 - optionIndex2;

			});

		}

		return (
			<div className="tc-material-input-display tc-material-input-display-checkboxes">
				{sortedValues.map((selectedValue, index) => {

					const matchingOption =
						sortedOptions.find((option) => option.value === selectedValue);

					return (
						<FormGroup
							key={selectedValue}
							check>
							<Input
								id={`option-display-${name}-selected-option-${index}`}
								type="checkbox"
								checked
								disabled />
							{' '}
							<Label
								for={`option-display-${name}-selected-option-${index}`}
								check>
								{matchingOption?.label || selectedValue}
							</Label>
						</FormGroup>
					);

				})}
				{(sortedValues.length === 0) &&
					<span className="tc-material-input-display-empty-value">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_TEXT_EMPTY_VALUE} />
					</span>
				}
			</div>
		);

	}

	// Handle multiple checkboxes being selected at the same time (if any)
	const handleInputChange = (optionID) => {

		const newValue = [...value];

		const selectedOption = sortedOptions.find((option) => option.id === optionID);

		const index = newValue.indexOf(selectedOption.value);

		// Unchecking the value (making it inactive)
		if (index >= 0) {

			newValue.splice(index, 1);

		// Checking the value (making it active)
		} else {

			newValue.push(selectedOption.value);

		}

		onChange(newValue);

	};

	return (
		<div
			role="group"
			aria-labelledby={name}
			aria-describedby={props['aria-describedby']}
			className={`tc-material-input-checkboxes ${isInvalid ? 'tc-material-input-invalid' : ''}`}>
			{sortedOptions.map((option) => (
				<FormGroup
					key={option.id}
					className={`
						tc-material-input-checkbox-option-${option.id}
						${value?.indexOf(option.value) >= 0
					? 'tc-material-input-checkbox-option-is-selected'
					: ''}`}
					check>
					<Input
						id={option.id}
						name={name}
						type="checkbox"
						checked={value && value.indexOf(option.value) >= 0}
						invalid={isInvalid}
						disabled={disabled}
						onChange={() => handleInputChange(option.id)} />
					{' '}
					<Label
						for={option.id}
						check>
						<div className="d-flex flex-column">
							<span>{option.label || option.value}</span>
							{option.help &&
								<FormText className="mt-0">
									{option.help}
								</FormText>
							}
						</div>
					</Label>
				</FormGroup>
			))}
		</div>
	);
};

CheckboxesInput.propTypes = propTypes;

export default CheckboxesInput;
